/**
 * Where the relation diagram puts things, and what the lines between them look like.
 *
 * Pure functions over the step's view nodes: no component, no DOM, no model. The diagram component
 * is then only rendering and interaction, and this arithmetic can be reasoned about — or exercised
 * — on its own.
 *
 * The shape of the drawing: depth decides the column, so a relation always sits to the right of the
 * node it hangs off, and a parent is centred against the block its children occupy. That makes the
 * picture stable — adding a relation extends a branch instead of reshuffling the ones already on
 * screen — which is what lets a curator keep their bearings while building.
 */

import { strategyCopyOf } from './relationsCopy';

/** Node box, the gap between siblings, and the horizontal room one level of depth takes. */
export const DiagramMetrics = Object.freeze({
  nodeWidth: 216,
  nodeHeight: 64,
  scalarNodeHeight: 46,
  siblingGap: 16,
  levelWidth: 300,
  canvasPaddingX: 48,
  canvasPaddingY: 44,
  /** How far the curve leaves its node before it starts bending towards the next one. */
  curveReach: 46,
});

export const ZoomRange = Object.freeze({
  min: 0.5,
  max: 1.4,
  step: 0.15,
  /** Fit never magnifies past this: a two-node definition blown up to 200% reads as broken. */
  fitMax: 1.25,
});

/** Line colours. Fan-out is warned about everywhere it appears, including here. */
export const EdgeStroke = Object.freeze({
  plain: '#9fb3ad',
  focused: '#03624c',
  fanOut: '#c77d00',
});

/**
 * Every node's box.
 *
 * The visited set guards the walk. The definition model cannot build a cycle — an edge's parent
 * must already exist and its own id is fresh — but a stored document could carry one, and without
 * the guard this would recurse until the stack gave out instead of drawing what it can.
 *
 * @param {Array} nodes the step's view nodes, flat
 * @returns {{positions: object, width: number, height: number}}
 */
export function layoutNodes(nodes) {
  const positions = {};
  const visited = new Set();
  let maxDepth = 0;

  const childrenOf = (id) => nodes.filter((node) => node.parentId === id && !visited.has(node.id));

  const place = (node, depth, top) => {
    const height = node.isScalar ? DiagramMetrics.scalarNodeHeight : DiagramMetrics.nodeHeight;
    const x = depth * DiagramMetrics.levelWidth;
    maxDepth = Math.max(maxDepth, depth);
    visited.add(node.id);

    const children = childrenOf(node.id);
    if (!children.length) {
      positions[node.id] = { x, y: top, height };
      return height;
    }

    let cursor = top;
    let total = 0;
    children.forEach((child) => {
      const childHeight = place(child, depth + 1, cursor);
      cursor += childHeight + DiagramMetrics.siblingGap;
      total += childHeight + DiagramMetrics.siblingGap;
    });
    total -= DiagramMetrics.siblingGap;

    positions[node.id] = { x, y: top + Math.max(0, total / 2 - height / 2), height };
    return Math.max(height, total);
  };

  const root = nodes.find((node) => !node.parentId);
  const height = root ? place(root, 0, 0) : 0;
  const width = root
    ? (maxDepth + 1) * DiagramMetrics.levelWidth - (DiagramMetrics.levelWidth - DiagramMetrics.nodeWidth) + 24
    : 0;

  return { positions, width, height };
}

/**
 * One curve per relation: from the right edge of the parent to the left edge of the child, with the
 * strategy word riding its midpoint.
 *
 * Focus is a reading aid, not a selection: the focused node's own connections brighten and thicken
 * and everything else fades, so a definition with a dozen relations can still be traced by eye.
 *
 * @param {Array} nodes the step's view nodes, flat
 * @param {object} layout the result of `layoutNodes`
 * @param {string|null} focusedNodeId the node the author clicked, if any
 */
export function linksOf(nodes, layout, focusedNodeId) {
  return nodes.reduce((collected, node) => {
    const from = layout.positions[node.parentId];
    const to = layout.positions[node.id];
    if (!from || !to) return collected;

    const startX = from.x + DiagramMetrics.nodeWidth;
    const startY = from.y + from.height / 2;
    const endX = to.x;
    const endY = to.y + to.height / 2;
    const reach = DiagramMetrics.curveReach;
    const focused = !!focusedNodeId && (focusedNodeId === node.id || focusedNodeId === node.parentId);

    return collected.concat({
      id: node.id,
      path: `M${startX} ${startY} C${startX + reach} ${startY}, ${endX - reach} ${endY}, ${endX} ${endY}`,
      endX,
      endY,
      stroke: node.shiftsGrain ? EdgeStroke.fanOut : (focused ? EdgeStroke.focused : EdgeStroke.plain),
      width: focused ? 2.4 : 1.7,
      dashed: node.shiftsGrain,
      dim: !!focusedNodeId && !focused,
      warn: node.shiftsGrain,
      label: strategyCopyOf(node.strategyKind).chip,
      labelX: (startX + endX) / 2,
      labelY: (startY + endY) / 2,
    });
  }, []);
}

/** A zoom the canvas will actually honour. */
export function clampZoom(value) {
  return Math.min(ZoomRange.max, Math.max(ZoomRange.min, Number(value.toFixed(2))));
}

/** The zoom at which the whole definition fits the canvas, never magnified past readable. */
export function zoomToFit(layout, canvasWidth, canvasHeight) {
  const horizontal = (canvasWidth - 24) / (layout.width + 60);
  const vertical = (canvasHeight - 24) / (layout.height + 40);
  return Math.min(ZoomRange.fitMax, clampZoom(Math.min(horizontal, vertical)));
}
